import Button, { ButtonType } from '../../../../../ui/Button/BlockButton';
import { useTheme } from '../../../../../../hooks/useTheme';
import { BREEDS } from '../breeds';
import { usePetForm, PET_NAME_MAX_LENGTH } from '../usePetForm';
import { PET_AGE_OPTIONS } from '../constants';
import styles from '../Fetch.module.css';
import { FetchSpeciesToggle } from './FetchSpeciesToggle';
import { FetchBreedCombobox } from './FetchBreedCombobox';
import type { FetchPetFormValues } from '../types';

interface FetchPetFormProps {
  onSubmit: (values: FetchPetFormValues) => void;
  loading?: boolean;
  initialEmail?: string;
  initialZip?: string;
  initialValues?: FetchPetFormValues | null;
  isMobile?: boolean;
}

const PRIVACY_URL = 'https://www.fetchpet.com/privacy-policy';
const TERMS_URL = 'https://www.fetchpet.com/terms-conditions';

export function FetchPetForm({
  onSubmit,
  loading,
  initialEmail,
  initialZip,
  initialValues,
  isMobile,
}: FetchPetFormProps) {
  const theme = useTheme();
  const f = usePetForm({ loading, initialEmail, initialZip, initialValues });

  return (
    <form
      className={styles.offerForm}
      style={{ fontFamily: theme.bodytextDefaultNone?.fontFamily }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!f.canSubmit) return;
        onSubmit(f.getValues());
      }}
    >
      <h2
        className={styles.offerFormHeading}
        style={{ fontFamily: theme.headersMediumNone?.fontFamily }}
      >
        Tell us about your pet
      </h2>

      <div className={styles.fields}>
        <FetchSpeciesToggle value={f.species} onChange={f.setSpecies} />

        <label className={styles.fieldBox}>
          <span className={styles.fieldLabel}>Pet&apos;s name</span>
          <input
            className={styles.fieldInput}
            maxLength={PET_NAME_MAX_LENGTH}
            value={f.petName}
            onChange={(e) => f.setPetName(e.target.value)}
            placeholder="e.g. Taro"
          />
        </label>

        <FetchBreedCombobox
          breeds={BREEDS[f.species]}
          value={f.breedId}
          onChange={f.setBreedId}
        />

        <div className={styles.fieldRow}>
          <label className={styles.fieldBox}>
            <span className={styles.fieldLabel}>How old?</span>
            <select
              className={`${styles.fieldSelect} ${f.age === '' ? styles.fieldSelectPlaceholder : ''}`}
              value={f.age}
              onChange={(e) => f.setAge(e.target.value)}
            >
              <option value="" disabled>
                Select an age
              </option>
              {PET_AGE_OPTIONS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.fieldBox}>
            <span className={styles.fieldLabel}>ZIP code</span>
            <input
              className={styles.fieldInput}
              inputMode="numeric"
              maxLength={5}
              value={f.zip}
              onChange={(e) =>
                f.setZip(e.target.value.replace(/\D/g, '').slice(0, 5))
              }
              placeholder="90210"
            />
          </label>
        </div>

        <label className={styles.fieldBox}>
          <span className={styles.fieldLabel}>Email</span>
          <input
            className={styles.fieldInput}
            type="email"
            value={f.emailDisplay}
            onFocus={f.beginEmailEdit}
            onBlur={f.endEmailEdit}
            onChange={(e) => f.setEmail(e.target.value)}
            placeholder="you@example.com"
          />
        </label>
      </div>

      <div className={isMobile ? styles.stickyActions : styles.actions}>
        <Button type={ButtonType.submit} big disabled={!f.canSubmit}>
          {loading ? 'Getting your price…' : 'See my price'}
        </Button>
      </div>

      <div className={styles.legalRow}>
        <a
          className={styles.legalLink}
          href={PRIVACY_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Privacy Policy
        </a>
        <span className={styles.legalDivider} aria-hidden />
        <a
          className={styles.legalLink}
          href={TERMS_URL}
          target="_blank"
          rel="noopener noreferrer"
        >
          Terms &amp; Condition
        </a>
      </div>
    </form>
  );
}
